import { useState, useRef, useMemo, useCallback, useEffect, useId, Children, cloneElement, isValidElement } from "react";

// Custom properties rather than literals, so Accessible mode can redefine the
// palette from one place. Every existing call site keeps working unchanged —
// these are still just strings assigned to inline style properties, the value
// is simply resolved by the browser instead of baked in. globalStyles below
// holds the actual colours, per accessibility mode.
export const G      = "var(--dp-accent)";
export const G_DARK = "var(--dp-accent-dark)";
export const G_BG   = "var(--dp-accent-bg)";

// The literal brand colours, for the few places that need a real value (e.g.
// computing a gradient or an SVG fill attribute) rather than a CSS reference.
export const BRAND_GREEN      = "#009640";
export const BRAND_GREEN_DARK = "#007a33";

export const REGIONS = [
  "eu-central-1", "eu-west-1", "eu-west-2", "eu-west-3", "eu-north-1",
  "us-east-1", "us-east-2", "us-west-1", "us-west-2",
  "ap-southeast-1", "ap-northeast-1",
];

export function Badge({ children, color = "green" }) {
  const colors = {
    green:  { background: "rgba(0,150,64,0.10)",   color: "#006b2d", borderColor: "rgba(0,150,64,0.30)" },
    amber:  { background: "rgba(217,119,6,0.10)",  color: "#92400e", borderColor: "rgba(217,119,6,0.30)" },
    red:    { background: "rgba(220,38,38,0.10)",  color: "#991b1b", borderColor: "rgba(220,38,38,0.30)" },
    blue:   { background: "rgba(37,99,235,0.10)",  color: "#1d4ed8", borderColor: "rgba(37,99,235,0.30)" },
    gray:   { background: "rgba(107,114,128,0.10)", color: "#374151", borderColor: "rgba(107,114,128,0.30)" },
  };
  return (
    // data-badge: nowrap on a desktop, so a state reads as one chip; below 768 px
    // globalStyles lets it wrap rather than widen its column past the screen.
    <span data-badge style={{
      display: "inline-block", padding: "2px 8px", borderRadius: 4,
      fontSize: 11, fontFamily: "'JetBrains Mono', monospace", fontWeight: 600,
      border: "1px solid", letterSpacing: "0.04em", whiteSpace: "nowrap",
      ...colors[color] ?? colors.gray,
    }}>
      {children}
    </span>
  );
}

export function Toggle({ checked, onChange, label, disabled = false }) {
  return (
    <button
      type="button"
      // role=switch + aria-checked is what makes this announce as "on"/"off"
      // rather than as an unlabelled button; the visual state is colour and
      // knob position alone, which conveys nothing to a screen reader.
      role="switch"
      aria-checked={!!checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      // The button is the target and draws nothing; the track is the picture.
      // On a phone globalStyles gives every switch min-height 44, and this way
      // that grows the hit area around a centred 44 x 24 track, not the pill
      // (ported from data-portal, 2026-10-20).
      style={{
        width: 44, height: 24, padding: 0, border: "none", background: "none",
        display: "inline-flex", alignItems: "center",
        cursor: disabled ? "not-allowed" : "pointer", opacity: disabled ? 0.5 : 1,
        flexShrink: 0,
      }}
    >
      <span data-toggle-track style={{
        width: 44, height: 24, borderRadius: 12, flexShrink: 0,
        background: checked ? G : "#9ca3af", position: "relative", transition: "background 0.2s",
        display: "block",
      }}>
        <span style={{
          position: "absolute", top: 3, left: checked ? 23 : 3,
          width: 18, height: 18, borderRadius: 9, background: "white",
          transition: "left 0.2s", display: "block", boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
        }} />
      </span>
    </button>
  );
}

export function ProgressBar({ value, label }) {
  const pct = Math.min(100, Math.max(0, value));
  return (
    <div
      // Without these the bar is a coloured div: its position conveys the
      // progress visually and nothing at all otherwise.
      role="progressbar"
      aria-valuenow={Math.round(pct)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      style={{ width: "100%", height: 8, borderRadius: 4, background: "#e5e7eb", overflow: "hidden" }}
    >
      <div style={{ width: `${pct}%`, height: "100%", background: G, transition: "width 0.15s linear" }} />
    </div>
  );
}

export function Field({ label, children, required, hint, style }) {
  const id     = useId();
  const hintId = `${id}-hint`;

  // The control is the first element child everywhere this is used. Wiring
  // the id here rather than at ~80 call sites is what makes every form field
  // actually announce its label — before this, a <label> with no htmlFor and
  // an <input> with no id meant every field in the app was unnamed to a
  // screen reader.
  let wired = false;
  const children_ = Children.map(children, child => {
    if (wired || !isValidElement(child)) return child;
    wired = true;
    return cloneElement(child, {
      id: child.props.id ?? id,
      "aria-describedby": hint ? hintId : child.props["aria-describedby"],
      "aria-required": required || undefined,
      required: child.props.required ?? required,
    });
  });

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 6, ...style }}>
      <label htmlFor={id} style={{ fontSize: 12, fontWeight: 600, color: "#374151", letterSpacing: "0.05em", textTransform: "uppercase" }}>
        {label}
        {/* The asterisk is decorative — aria-required on the control is what
            actually conveys this, so it isn't read out twice. */}
        {required && <span aria-hidden="true" style={{ color: "#b91c1c", marginLeft: 4 }}>*</span>}
      </label>
      {children_}
      {hint && <span id={hintId} style={{ fontSize: 11, color: "#6b7280" }}>{hint}</span>}
    </div>
  );
}

const inputBase = {
  background: "#ffffff", border: "1px solid #d1d5db", borderRadius: 8,
  color: "#1a1a1a", padding: "10px 13px", fontSize: 14, outline: "none",
  width: "100%", boxSizing: "border-box", fontFamily: "inherit",
  transition: "border-color 0.2s, box-shadow 0.2s",
};

export function Input(props) {
  const [focused, setFocused] = useState(false);
  return (
    <input
      {...props}
      style={{
        ...inputBase,
        borderColor: focused ? G : "#d1d5db",
        boxShadow: focused ? `0 0 0 3px rgba(0,150,64,0.12)` : "none",
        ...props.style,
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

export function Select({ options, ...props }) {
  const [focused, setFocused] = useState(false);
  return (
    <select
      {...props}
      style={{
        ...inputBase,
        borderColor: focused ? G : "#d1d5db",
        boxShadow: focused ? `0 0 0 3px rgba(0,150,64,0.12)` : "none",
        cursor: "pointer",
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    >
      <option value="">— select —</option>
      {options.map(o =>
        typeof o === "string"
          ? <option key={o} value={o}>{o}</option>
          : <option key={o.value} value={o.value}>{o.label}</option>
      )}
    </select>
  );
}

export function Textarea(props) {
  const [focused, setFocused] = useState(false);
  return (
    <textarea
      rows={3}
      {...props}
      style={{
        ...inputBase,
        borderColor: focused ? G : "#d1d5db",
        boxShadow: focused ? `0 0 0 3px rgba(0,150,64,0.12)` : "none",
        resize: "vertical",
      }}
      onFocus={() => setFocused(true)}
      onBlur={() => setFocused(false)}
    />
  );
}

export function Card({ children, style }) {
  // data-card="default" lets the phone block narrow the padding; a caller that
  // set its own (the `padding: 0` table cards) gets no hook and keeps it.
  const ownPadding = style && style.padding !== undefined;
  return (
    <div data-card={ownPadding ? "own-padding" : "default"} style={{
      background: "#ffffff", border: "1px solid #e5e7eb", borderRadius: 16,
      padding: "28px 32px", boxShadow: "0 1px 4px rgba(0,0,0,0.06)",
      ...style,
    }}>
      {children}
    </div>
  );
}

export function Btn({ children, onClick, variant = "primary", disabled, type = "button", style: extraStyle }) {
  // White on the brand green #009640 is only 3.87:1 — below the 4.5:1 WCAG AA
  // needs for text this size. G_DARK (#007a33) reaches 5.3:1 and is the same
  // colour family, so the button keeps its identity and becomes readable.
  // Likewise #dc2626 on white is fine as a fill but its white label is not.
  // Every variant greys out when disabled; secondary did not, so a click on it
  // (disabled while the request runs) looked like nothing happened (unio
  // 2026-10-22). #4b5563 on #f3f4f6 is 6.9:1.
  const styles = {
    primary:   { background: disabled ? "#e5e7eb" : G_DARK,   color: disabled ? "#6b7280" : "#fff",     border: "none" },
    secondary: { background: disabled ? "#f3f4f6" : "#ffffff", color: disabled ? "#4b5563" : "#374151", border: disabled ? "1px solid #d1d5db" : "1px solid #9ca3af" },
    danger:    { background: disabled ? "#e5e7eb" : "#b91c1c", color: disabled ? "#6b7280" : "#fff",     border: "none" },
    ghost:     { background: disabled ? "transparent" : G_BG,  color: disabled ? "#6b7280" : G_DARK,    border: `1px solid rgba(0,150,64,0.5)` },
  };
  return (
    <button
      type={type}
      onClick={onClick}
      disabled={disabled}
      style={{
        padding: "9px 20px", borderRadius: 8, fontSize: 14, fontWeight: 600,
        cursor: disabled ? "default" : "pointer", fontFamily: "inherit",
        transition: "all 0.15s", display: "inline-flex", alignItems: "center", gap: 6,
        ...styles[variant],
        ...extraStyle,
      }}
    >
      {children}
    </button>
  );
}

export function StatusBadge({ status }) {
  // Each state carries a glyph as well as a colour, so "ready" and "failed"
  // stay distinguishable to anyone who can't separate green from red, and in
  // print or greyscale (WCAG 1.4.1 — colour is never the only carrier). The
  // word is already there; the glyph makes the scan-at-a-glance case work too.
  const map = {
    active:     { label: "Active",      color: "green", glyph: "●" },
    completed:  { label: "Completed",   color: "blue",  glyph: "✓" },
    archived:   { label: "Archived",    color: "gray",  glyph: "▪" },
    queued:     { label: "Queued",      color: "amber", glyph: "…" },
    processing: { label: "Processing",  color: "amber", glyph: "◐" },
    running:    { label: "Running",     color: "amber", glyph: "◐" },
    ready:      { label: "Ready",       color: "green", glyph: "✓" },
    failed:     { label: "Failed",      color: "red",   glyph: "✕" },
    cancelled:  { label: "Cancelled",   color: "gray",  glyph: "⊘" },
    pending:    { label: "Pending",     color: "gray",  glyph: "…" },
  };
  const s = map[status] ?? { label: status, color: "gray", glyph: "" };
  return (
    <Badge color={s.color}>
      {s.glyph && <span aria-hidden="true" style={{ marginRight: 4 }}>{s.glyph}</span>}
      {s.label}
    </Badge>
  );
}

export function Spinner({ size = 14 }) {
  return (
    <span style={{
      width: size, height: size, borderRadius: "50%",
      border: `2px solid rgba(0,0,0,0.08)`,
      borderTopColor: G,
      display: "inline-block",
      animation: "spin 0.8s linear infinite",
    }} />
  );
}

function EyeIcon({ crossed }) {
  return crossed ? (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17.94 17.94A10.07 10.07 0 0112 20c-7 0-11-8-11-8a18.45 18.45 0 015.06-5.94"/>
      <path d="M9.9 4.24A9.12 9.12 0 0112 4c7 0 11 8 11 8a18.5 18.5 0 01-2.16 3.19"/>
      <path d="M14.12 14.12a3 3 0 11-4.24-4.24"/>
      <line x1="1" y1="1" x2="23" y2="23"/>
    </svg>
  ) : (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
      <circle cx="12" cy="12" r="3"/>
    </svg>
  );
}

export function PasswordInput(props) {
  const [show, setShow]       = useState(false);
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ position: "relative" }}>
      <input
        {...props}
        type={show ? "text" : "password"}
        style={{
          ...inputBase,
          paddingRight: 40,
          borderColor: focused ? G : "#d1d5db",
          boxShadow: focused ? `0 0 0 3px rgba(0,150,64,0.12)` : "none",
          ...props.style,
        }}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
      />
      <button
        type="button"
        tabIndex={-1}
        onClick={() => setShow(s => !s)}
        style={{
          position: "absolute", right: 10, top: "50%", transform: "translateY(-50%)",
          background: "none", border: "none", cursor: "pointer", padding: 4,
          color: "#6b7280", display: "flex", alignItems: "center", lineHeight: 0,
        }}
      >
        <EyeIcon crossed={show} />
      </button>
    </div>
  );
}

// Mirrors the backend's password_policy_errors so a weak password is caught
// before the round trip. The backend stays the authority — it re-checks and
// returns the definitive message — this only avoids a pointless submit.
export const MIN_PASSWORD_LENGTH = 12;

export function passwordPolicyChecks(password, username) {
  const pw = password || "";
  const classes = [/[a-z]/, /[A-Z]/, /[0-9]/, /[^A-Za-z0-9]/].filter(re => re.test(pw)).length;
  const u = (username || "").toLowerCase();
  return [
    { label: `At least ${MIN_PASSWORD_LENGTH} characters`, ok: pw.length >= MIN_PASSWORD_LENGTH },
    { label: "Three of: lower-case, upper-case, digits, symbols", ok: classes >= 3 },
    { label: "Does not contain the username", ok: !(u.length >= 4 && pw.toLowerCase().includes(u)) },
    { label: "Uses more than a handful of different characters", ok: new Set(pw).size >= 5 },
  ];
}

export function passwordIsAcceptable(password, username) {
  return passwordPolicyChecks(password, username).every(c => c.ok);
}

export function PasswordRequirements({ password, username }) {
  if (!password) return null;
  const checks = passwordPolicyChecks(password, username);
  return (
    <ul style={{ margin: "8px 0 0", padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 3 }}>
      {checks.map(c => (
        // The tick/cross carries the state on its own, so this reads the same
        // in monochrome or to anyone who can't separate the two colours.
        <li key={c.label} style={{ fontSize: 11, color: c.ok ? "#15803d" : "#6b7280", display: "flex", gap: 6 }}>
          <span aria-hidden="true">{c.ok ? "✓" : "○"}</span>
          <span>{c.label}{c.ok ? " — met" : ""}</span>
        </li>
      ))}
    </ul>
  );
}

export function PasswordStrength({ password }) {
  if (!password) return null;
  let score = 0;
  if (password.length >= 8)            score++;
  if (password.length >= 12)           score++;
  if (/[A-Z]/.test(password))          score++;
  if (/[a-z]/.test(password))          score++;
  if (/[0-9]/.test(password))          score++;
  if (/[^A-Za-z0-9]/.test(password))   score++;
  const level  = score <= 2 ? 0 : score <= 3 ? 1 : score <= 4 ? 2 : 3;
  const labels = ["Weak", "Fair", "Good", "Strong"];
  const colors = ["#dc2626", "#d97706", "#2563eb", "#009640"];
  return (
    <div style={{ marginTop: 6 }}>
      <div style={{ height: 3, background: "#e5e7eb", borderRadius: 2, overflow: "hidden" }}>
        <div style={{
          height: "100%", borderRadius: 2,
          width: `${(level + 1) * 25}%`,
          background: colors[level],
          transition: "width 0.25s, background 0.25s",
        }} />
      </div>
      <span style={{ fontSize: 11, color: colors[level], fontWeight: 600, marginTop: 3, display: "inline-block" }}>
        {labels[level]}
      </span>
    </div>
  );
}

// ── Sortable / resizable table headers ─────────────────────────────────────────
// Shared across every table view (Users, Groups, Log, Projects, Submissions,
// Results): click a header to sort by that column (click again to reverse),
// drag the handle on its right edge to resize. Column widths are held in
// pixels via useColumnWidths and the table uses tableLayout:"fixed" with an
// explicit width so it can grow past its container — wrap it in a div with
// overflowX:"auto" so wide/narrow columns scroll instead of clipping content
// (e.g. row-action buttons) against the container edge.

export function useSortableColumns(rows, accessors, initialKey = null, initialDir = "asc") {
  // Held as one object (not two separate useState calls) so toggling is a
  // single setState — flipping dir from inside the key updater would rely on
  // React's eager-bailout re-invoking the updater and silently cancel itself
  // out on every other click.
  const [sort, setSort] = useState({ key: initialKey, dir: initialDir });
  const { key: sortKey, dir: sortDir } = sort;

  const toggleSort = useCallback(key => {
    setSort(prev => prev.key === key
      ? { key, dir: prev.dir === "asc" ? "desc" : "asc" }
      : { key, dir: "asc" });
  }, []);

  const sorted = useMemo(() => {
    if (!sortKey) return rows;
    const get = accessors[sortKey] || (r => r[sortKey]);
    const copy = [...rows].sort((a, b) => {
      const av = get(a), bv = get(b);
      if (av == null && bv == null) return 0;
      if (av == null) return 1;
      if (bv == null) return -1;
      if (typeof av === "number" && typeof bv === "number") return av - bv;
      return String(av).localeCompare(String(bv), undefined, { numeric: true, sensitivity: "base" });
    });
    return sortDir === "desc" ? copy.reverse() : copy;
  }, [rows, accessors, sortKey, sortDir]);

  return { sorted, sortKey, sortDir, toggleSort };
}

export function useColumnWidths(defaults) {
  const [widths, setWidths] = useState(defaults);
  const setWidth = useCallback((key, w) => setWidths(prev => ({ ...prev, [key]: w })), []);
  const totalWidth = useMemo(() => Object.values(widths).reduce((a, b) => a + b, 0), [widths]);
  return { widths, setWidth, totalWidth };
}

export function Th({ label, align = "left", sortKey, activeKey, dir, onSort, width, onWidth, minWidth = 50, style }) {
  const ref = useRef(null);
  const [resizing, setResizing] = useState(false);
  const sortable = !!sortKey && !!onSort;
  const active = sortable && activeKey === sortKey;

  const startResize = e => {
    e.preventDefault();
    e.stopPropagation();
    const startX = e.clientX;
    const startWidth = ref.current.getBoundingClientRect().width;
    setResizing(true);
    const onMove = ev => onWidth(Math.max(minWidth, Math.round(startWidth + (ev.clientX - startX))));
    const onUp = () => {
      setResizing(false);
      document.removeEventListener("mousemove", onMove);
      document.removeEventListener("mouseup", onUp);
    };
    document.addEventListener("mousemove", onMove);
    document.addEventListener("mouseup", onUp);
  };

  return (
    <th
      ref={ref}
      scope="col"
      // Announces the current sort and lets a screen-reader user know the
      // column is sortable at all; "none" on the inactive ones is what makes
      // the active one stand out rather than every column claiming a state.
      aria-sort={sortable ? (active ? (dir === "asc" ? "ascending" : "descending") : "none") : undefined}
      onClick={sortable ? () => onSort(sortKey) : undefined}
      style={{
        position: "relative", padding: "10px 16px", textAlign: align,
        fontSize: 11, fontWeight: 700, color: "#4b5563", textTransform: "uppercase",
        letterSpacing: "0.05em", whiteSpace: "nowrap", userSelect: "none",
        cursor: sortable ? "pointer" : "default",
        width: width != null ? `${width}px` : undefined,
        ...style,
      }}
    >
      <span style={{
        display: "inline-flex", alignItems: "center", gap: 5, width: "100%",
        justifyContent: align === "right" ? "flex-end" : align === "center" ? "center" : "flex-start",
      }}>
        {/* A real button when sortable, so sorting is reachable by keyboard —
            a click handler on the <th> alone never is. */}
        {sortable ? (
          <button
            type="button"
            onClick={e => { e.stopPropagation(); onSort(sortKey); }}
            style={{
              background: "none", border: "none", padding: 0, font: "inherit", color: "inherit",
              letterSpacing: "inherit", textTransform: "inherit", cursor: "pointer",
            }}
          >
            {label}
          </button>
        ) : label}
        {sortable && (
          <span aria-hidden="true" style={{ display: "inline-flex", flexDirection: "column", gap: 1, flexShrink: 0 }}>
            <span style={{ fontSize: 7, lineHeight: "7px", color: active && dir === "asc" ? G_DARK : "#9ca3af" }}>▲</span>
            <span style={{ fontSize: 7, lineHeight: "7px", color: active && dir === "desc" ? G_DARK : "#9ca3af" }}>▼</span>
          </span>
        )}
      </span>
      {onWidth && (
        <span
          onMouseDown={startResize}
          onClick={e => e.stopPropagation()}
          title="Drag to resize"
          // Pointer-only by nature, and purely cosmetic — hidden rather than
          // presented as something a keyboard user could act on but can't.
          aria-hidden="true"
          style={{ position: "absolute", top: 0, right: -4, bottom: 0, width: 8, cursor: "col-resize", zIndex: 2 }}
        >
          <span style={{
            position: "absolute", top: 0, bottom: 0, left: 3, width: 2,
            background: resizing ? G : "transparent", transition: "background 0.1s",
          }} />
        </span>
      )}
    </th>
  );
}

// Confirmation for something irreversible. The name has to be typed out: a
// plain "are you sure?" is dismissed reflexively, and this destroys data that
// cannot be recovered. `blockers` is what stops the action being offered at
// all — a reason shown up front beats an error after the click.
// Focus starts on the confirmation field (on the dialog itself when there are
// blockers), stays inside while it is open, and goes back to the opener on
// close — useModalFocus below.
export function ConfirmDialog({
  title, confirmWord, confirmLabel = "Delete", busyLabel, tone = "danger", busy = false,
  blockers = [], children, onConfirm, onClose,
}) {
  const [typed, setTyped]     = useState("");
  const [focused, setFocused] = useState(false);
  const field = useRef(null);
  const id    = useId();
  // No confirmWord means no typing is required (data-portal 2026-10-20): comparing
  // against `undefined` gave a dialog that could never be confirmed.
  const needsTyping = typeof confirmWord === "string" && confirmWord.length > 0;
  const ready = (!needsTyping || typed.trim() === confirmWord) && blockers.length === 0 && !busy;
  const danger = tone === "danger";

  return (
    <Sheet labelledBy={`${id}-t`} onClose={onClose} initialFocus={needsTyping && blockers.length === 0 ? field : null}>
        <h2 id={`${id}-t`} style={{ margin: "0 0 12px", fontSize: 18, fontWeight: 700, color: danger ? "#b91c1c" : "#0b6b33" }}>{title}</h2>

        {children}

        {blockers.length > 0 ? (
          <div role="alert" style={{ marginTop: 16, padding: "12px 14px", borderRadius: 8, background: "rgba(217,119,6,0.08)", border: "1px solid rgba(217,119,6,0.35)", fontSize: 13, color: "#92400e" }}>
            <strong>{danger ? "This cannot be deleted yet." : "This cannot be done yet."}</strong>
            <ul style={{ margin: "6px 0 0", paddingLeft: 18 }}>
              {blockers.map((b, i) => <li key={i} style={{ marginTop: 4 }}>{b}</li>)}
            </ul>
          </div>
        ) : needsTyping ? (
          <div style={{ marginTop: 16 }}>
            <label htmlFor={`${id}-c`} style={{ fontSize: 13, color: "#374151", display: "block", marginBottom: 6 }}>
              Type <strong style={{ fontFamily: "'JetBrains Mono', monospace", overflowWrap: "anywhere" }}>{confirmWord}</strong> to confirm
            </label>
            {/* inputBase sets outline: none, so the field draws its own focus
                indicator, as Input does: the accent border and a ring. */}
            <input
              ref={field}
              id={`${id}-c`}
              value={typed}
              onChange={e => setTyped(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              autoComplete="off"
              style={{
                ...inputBase,
                borderColor: ready ? "#b91c1c" : focused ? G : "#9ca3af",
                boxShadow: focused ? `0 0 0 3px ${ready ? "rgba(185,28,28,0.25)" : "rgba(0,150,64,0.25)"}` : "none",
              }}
            />
          </div>
        ) : null}

        <SheetActions>
          <Btn variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn variant={danger ? "danger" : "primary"} disabled={!ready} onClick={onConfirm}>
            {busy ? <><Spinner /> {busyLabel || (danger ? "Deleting…" : "Working…")}</> : confirmLabel}
          </Btn>
        </SheetActions>
    </Sheet>
  );
}

// The keyboard side of a modal dialog, ported from data-portal's useModalFocus
// (its Shared.jsx, 2026-09-30): Escape closes it; Tab and Shift+Tab cycle
// inside it — aria-modal promises a modal, and without a trap the keyboard
// walks out to the page behind; on close, focus goes back to whatever had it
// when the dialog opened instead of falling to <body>. `initial` is an
// optional ref to the element that takes focus first (the dialog node when it
// is not rendered). The effect runs once per opening: onClose is read through
// a ref, so a parent that passes a new inline onClose on every render does not
// pull focus back to the start. Found missing by unio's accessibility pass of
// 2026-10-04.
export function useModalFocus(ref, onClose, initial = null) {
  const opener = useRef(null);
  const close  = useRef(onClose);
  close.current = onClose;

  useEffect(() => {
    opener.current = document.activeElement;
    const node = ref.current;
    (initial?.current || node)?.focus();

    const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), '
                    + 'select:not([disabled]), textarea:not([disabled]), '
                    + '[tabindex]:not([tabindex="-1"])';

    const onKey = e => {
      if (e.key === "Escape") { close.current(); return; }
      if (e.key !== "Tab" || !node) return;
      const items = [...node.querySelectorAll(FOCUSABLE)];
      if (!items.length) { e.preventDefault(); node.focus(); return; }
      const first = items[0];
      const last  = items[items.length - 1];
      const here  = document.activeElement;
      if (!node.contains(here))                                 { e.preventDefault(); first.focus(); }
      else if (e.shiftKey && (here === first || here === node)) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && here === last)                    { e.preventDefault(); first.focus(); }
    };

    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("keydown", onKey);
      const back = opener.current;
      // Only if it is still on the page: a dialog opened from a row that the
      // action then removed has nowhere to go back to, and focusing a detached
      // node throws the focus away instead of restoring it.
      if (back && typeof back.focus === "function" && document.contains(back)) back.focus();
    };
  }, [ref, initial]);
}

/**
 * The one overlay: a centred card on a desktop, a sheet on a phone (ported from
 * data-portal's Shared.jsx, 2026-10-20).
 *
 * What a caller gets without asking: the backdrop (a click on it closes),
 * `useModalFocus` (Escape, the Tab trap, focus given back; `initialFocus` is an
 * optional ref to the element that takes focus first), `aria-modal`, and a name
 * — `labelledBy` (the id of the caller's heading) or `label`. Below 768 px the
 * card becomes a full-width sheet from the bottom bounded by `100dvh` (`90vh`
 * let the iOS toolbars cover the buttons), padded clear of the safe areas, its
 * heading wraps and a `SheetActions` row stacks full width. `variant="full"`
 * fills the whole screen there instead — for content that is a page of its own.
 * An app keeps every fixed overlay here: data-portal's overlayContract.test.js
 * refuses `position: "fixed"` and `role="dialog"` anywhere else.
 */
export function Sheet({
  labelledBy, label, onClose, maxWidth = 560, variant = "bottom",
  zIndex = 220, initialFocus = null, style, children,
}) {
  const ref = useRef(null);
  useModalFocus(ref, onClose, initialFocus);

  return (
    <div
      data-sheet-overlay
      onClick={e => { if (e.target === e.currentTarget) onClose(); }}
      style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex, display: "flex", alignItems: "center", justifyContent: "center", padding: 24 }}
    >
      <div ref={ref} data-sheet={variant} role="dialog" aria-modal="true"
        aria-labelledby={labelledBy} aria-label={labelledBy ? undefined : label} tabIndex={-1}
        style={{ background: "#fff", borderRadius: 16, border: "1px solid #e5e7eb", maxWidth, width: "100%", maxHeight: "90vh", overflowY: "auto", padding: "24px 28px", boxShadow: "0 8px 32px rgba(0,0,0,0.2)", boxSizing: "border-box", ...style }}>
        {children}
      </div>
    </div>
  );
}

/**
 * A sheet's heading and its close button, on one row that wraps: the title may
 * shrink and wrap, and the "×" is a named 44px target. `id` is what the Sheet's
 * `labelledBy` points at.
 */
export function SheetHeader({ id, title, onClose, closeLabel = "Close" }) {
  return (
    <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 12, marginBottom: 12 }}>
      <h2 id={id} style={{ margin: 0, minWidth: 0, flex: "1 1 auto", fontSize: 17, fontWeight: 700, overflowWrap: "anywhere" }}>
        {title}
      </h2>
      <button type="button" onClick={onClose} aria-label={closeLabel}
              style={{ flex: "0 0 auto", minWidth: 44, minHeight: 44, margin: "-10px -10px 0 0",
                       border: "none", borderRadius: 8, background: "none", cursor: "pointer",
                       color: "#6b7280", fontSize: 22, lineHeight: 1, fontFamily: "inherit" }}>
        <span aria-hidden="true">×</span>
      </button>
    </div>
  );
}

/** A sheet's button row: right-aligned on a desktop, stacked full width on a phone. */
export function SheetActions({ children, style }) {
  return (
    <div data-sheet-actions style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20, ...style }}>
      {children}
    </div>
  );
}

/**
 * A row's secondary actions, behind one "More…" button (ported from data-portal,
 * 2026-10-20). The caller keeps the primary actions on the row (inside
 * `data-row-actions`) and hands the rest here as `items`
 * (`{ label, onSelect, tone, title }`; a falsy entry renders nothing, no item at
 * all renders no button).
 *
 * A real menu to a keyboard: Enter, Space or ArrowDown on the trigger opens it on
 * the first item; the arrows, Home and End move and wrap; Escape closes it and
 * gives focus back; Tab closes it. Choosing an item gives focus back to the
 * trigger *before* acting, so a Sheet the item opens returns there. Positioned
 * `absolute` against its own wrapper, never `fixed`. `context` names the row to
 * a screen reader ("More… Terms").
 */
export function RowActionsMenu({ items, label = "More…", context }) {
  const shown = (items || []).filter(Boolean);
  const [open, setOpen] = useState(false);
  const wrap = useRef(null);
  const trigger = useRef(null);
  const menu = useRef(null);
  const focusFirst = useRef(false);
  const id = useId();

  const itemNodes = () => [...(menu.current?.querySelectorAll('[role="menuitem"]') || [])];

  useEffect(() => {
    if (!open) return undefined;
    if (focusFirst.current) {
      focusFirst.current = false;
      menu.current?.querySelector('[role="menuitem"]')?.focus();
    }
    const away = e => { if (wrap.current && !wrap.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", away);
    return () => document.removeEventListener("mousedown", away);
  }, [open]);

  if (shown.length === 0) return null;

  const close = () => { setOpen(false); trigger.current?.focus(); };

  const onTriggerKey = e => {
    if (e.key === "Enter" || e.key === " " || e.key === "ArrowDown") {
      // preventDefault: Enter would click the button too, and toggle it shut.
      e.preventDefault();
      focusFirst.current = true;
      if (open) itemNodes()[0]?.focus(); else setOpen(true);
    }
  };

  const onMenuKey = e => {
    const nodes = itemNodes();
    const at = nodes.indexOf(document.activeElement);
    const go = i => { e.preventDefault(); nodes[(i + nodes.length) % nodes.length]?.focus(); };
    if (e.key === "ArrowDown") go(at + 1);
    else if (e.key === "ArrowUp") go(at - 1);
    else if (e.key === "Home") go(0);
    else if (e.key === "End") go(nodes.length - 1);
    else if (e.key === "Escape") { e.preventDefault(); e.stopPropagation(); close(); }
    else if (e.key === "Tab") setOpen(false);
  };

  return (
    <span ref={wrap} style={{ position: "relative", display: "inline-block" }}>
      <button ref={trigger} type="button" aria-haspopup="menu" aria-expanded={open}
              aria-label={context ? `${label} ${context}` : undefined}
              aria-controls={open ? `${id}-m` : undefined}
              onClick={() => setOpen(o => !o)} onKeyDown={onTriggerKey}
              style={{ padding: "4px 10px", fontSize: 12, fontWeight: 600, borderRadius: 8,
                       border: "1px solid #9ca3af", background: "#fff", color: "#374151",
                       cursor: "pointer", fontFamily: "inherit" }}>
        {label}
      </button>
      {open && (
        <div ref={menu} id={`${id}-m`} role="menu" aria-label={context ? `Actions for ${context}` : "Actions"}
             onKeyDown={onMenuKey}
             style={{ position: "absolute", right: 0, top: "calc(100% + 4px)", zIndex: 30,
                      minWidth: 200, maxWidth: "min(280px, calc(100vw - 32px))", padding: 4,
                      background: "#fff", border: "1px solid #e5e7eb", borderRadius: 10,
                      boxShadow: "0 8px 24px rgba(0,0,0,0.15)", textAlign: "left" }}>
          {shown.map(it => (
            <button key={it.label} type="button" role="menuitem" tabIndex={-1} title={it.title}
                    onClick={() => { close(); it.onSelect(); }}
                    style={{ display: "flex", alignItems: "center", width: "100%", minHeight: 44,
                             padding: "8px 12px", border: "none", borderRadius: 6, background: "none",
                             textAlign: "left", fontSize: 14, fontFamily: "inherit", cursor: "pointer",
                             whiteSpace: "normal", overflowWrap: "anywhere",
                             color: it.tone === "danger" ? "#b91c1c" : "#374151" }}>
              {it.label}
            </button>
          ))}
        </div>
      )}
    </span>
  );
}

/**
 * An expandable section (ported from data-portal, 2026-10-20). A real <button>
 * with aria-expanded. The children are NOT rendered while closed — panels that
 * fetch on mount would otherwise all fetch on every visit. `level` indents
 * nesting; below 768 px the indent goes (data-collapsible-body) and the
 * subtitle moves under the title.
 */
export function Collapsible({ title, subtitle, badge, defaultOpen = false, children, style, level = 0 }) {
  const [open, setOpen] = useState(defaultOpen);
  const id = `collapsible-${useId().replace(/[^a-zA-Z0-9_-]/g, "")}`;
  return (
    <div style={{ border: "1px solid #e5e7eb", borderRadius: 10, marginBottom: 10,
                  background: level ? "#fbfbfc" : "#fff", ...style }}>
      <button type="button"
              data-collapsible-header
              aria-expanded={open}
              aria-controls={id}
              onClick={() => setOpen(o => !o)}
              style={{ display: "flex", alignItems: "center", gap: 10, width: "100%",
                       padding: level ? "9px 14px" : "12px 16px", background: "none",
                       border: "none", cursor: "pointer", textAlign: "left",
                       fontFamily: "inherit", color: "#111827" }}>
        <span aria-hidden="true" style={{ color: "#6b7280", fontSize: 12, width: 12,
                                          transform: open ? "rotate(90deg)" : "none",
                                          transition: "transform 120ms" }}>▶</span>
        <strong style={{ fontSize: level ? 13 : 14 }}>{title}</strong>
        {badge}
        {subtitle && (
          <span data-collapsible-subtitle
                style={{ color: "#6b7280", fontSize: 12, fontWeight: 400 }}>{subtitle}</span>
        )}
      </button>
      {open && (
        <div id={id} data-collapsible-body
             style={{ padding: level ? "0 14px 12px 38px" : "0 16px 16px 38px" }}>
          {children}
        </div>
      )}
    </div>
  );
}

// The reason a control is disabled, printed only where there is no hover to show
// the wrapper's `title` (see `[data-touch-reason]` in globalStyles). Put it in the
// same flex row as the control; it takes a line of its own. Rendered for
// disabled controls only — an enabled one needs no excuse.
export function TouchReason({ children }) {
  return <span data-touch-reason="">{children}</span>;
}

/**
 * A screen that does not work at phone width — a wide editor, a diagram — says so
 * below 768 px instead of being squeezed, and offers "Show it anyway" (the choice
 * is kept for the session). Above the breakpoint, and in jsdom (no matchMedia),
 * the children render as they are. `what` names the screen in the note.
 */
export function NeedsLargerScreen({ what = "This screen", children }) {
  const narrow = useNarrowScreen();
  const key = `boku-ui:show-anyway:${what}`;
  const [anyway, setAnyway] = useState(() => {
    try { return sessionStorage.getItem(key) === "1"; } catch { return false; }
  });
  if (!narrow || anyway) return children;
  const show = () => {
    try { sessionStorage.setItem(key, "1"); } catch { /* private mode: for this view only */ }
    setAnyway(true);
  };
  return (
    <div data-needs-larger-screen role="note"
         style={{ padding: "16px 18px", borderRadius: 12, background: "#f9fafb", border: "1px solid #e5e7eb",
                  fontSize: 14, color: "#374151" }}>
      <p style={{ margin: "0 0 12px" }}>
        <strong>{what} needs a larger screen.</strong> It is laid out for a tablet or a computer; on a
        phone it scrolls sideways.
      </p>
      <Btn variant="secondary" onClick={show}>Show it anyway</Btn>
    </div>
  );
}

// True below the 768 px breakpoint of globalStyles, following resizes. False
// where there is no matchMedia (jsdom), so tests see the desktop layout.
export function useNarrowScreen(query = "(max-width: 768px)") {
  const get = () => typeof window !== "undefined" && typeof window.matchMedia === "function"
    && window.matchMedia(query).matches;
  const [narrow, setNarrow] = useState(get);
  useEffect(() => {
    if (typeof window === "undefined" || typeof window.matchMedia !== "function") return undefined;
    const mq = window.matchMedia(query);
    const on = () => setNarrow(mq.matches);
    on();
    if (mq.addEventListener) mq.addEventListener("change", on); else mq.addListener(on);
    return () => { if (mq.removeEventListener) mq.removeEventListener("change", on); else mq.removeListener(on); };
  }, [query]);
  return narrow;
}

export const globalStyles = `
  :root {
    --dp-accent:      #009640;   /* BOKU green */
    --dp-accent-dark: #007a33;
    --dp-accent-bg:   rgba(0,150,64,0.08);
    --dp-scale:       1;
  }

  /* High contrast: darker accents (7:1 against white, WCAG 1.4.6 AAA),
     heavier borders, and no faint tints that disappear on a dim screen. */
  :root[data-a11y-contrast="high"] {
    --dp-accent:      #00551f;
    --dp-accent-dark: #003d16;
    --dp-accent-bg:   #e3f2e8;
  }
  :root[data-a11y-contrast="high"] body { color: #000000; }
  :root[data-a11y-contrast="high"] input,
  :root[data-a11y-contrast="high"] select,
  :root[data-a11y-contrast="high"] textarea,
  :root[data-a11y-contrast="high"] button {
    border-width: 2px !important;
    border-style: solid !important;
    border-color: #1a1a1a !important;
  }
  :root[data-a11y-contrast="high"] :focus-visible {
    outline-width: 4px;
  }

  /* Colour-blind-safe: the Okabe–Ito pairing, which stays distinguishable
     under deuteranopia and protanopia where green/red does not. Status text
     also carries a glyph (see StatusBadge), so colour is never the only cue
     (WCAG 1.4.1). */
  :root[data-a11y-palette="colorblind-safe"] {
    --dp-accent:      #0072b2;
    --dp-accent-dark: #005587;
    --dp-accent-bg:   rgba(0,114,178,0.10);
  }

  /* Text scaling. A root font-size would only move rem-based sizes, and this
     codebase sizes everything in px — so the document is zoomed instead.
     That keeps every proportion intact (and the layout reflows, since the
     1100px container scales with it) without rewriting units in 80 files.
     Browser zoom still works on top of this, which is what WCAG 1.4.4
     actually requires; this is the in-app convenience. */
  body { zoom: var(--dp-scale); }
  :root[data-a11y-targets="large"] button,
  :root[data-a11y-targets="large"] a,
  :root[data-a11y-targets="large"] [role="button"],
  :root[data-a11y-targets="large"] [role="switch"] {
    min-height: 44px;
    min-width: 44px;
  }

  :root[data-a11y-motion="reduced"] *,
  :root[data-a11y-motion="reduced"] *::before,
  :root[data-a11y-motion="reduced"] *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
  }

  @keyframes spin { to { transform: rotate(360deg); } }
  * { box-sizing: border-box; }
  select option { background: #ffffff; color: #1a1a1a; }

  /* A visible focus indicator on everything that can take focus. Buttons here
     are styled inline and several sit on coloured backgrounds, so the ring is
     drawn with an offset outline plus a white halo — that keeps it visible on
     the green top bar as well as on white cards (WCAG 2.4.7 / 2.4.11). */
  :focus-visible {
    outline: 3px solid #1a1a1a;
    outline-offset: 2px;
    border-radius: 4px;
  }
  [data-topbar] :focus-visible {
    outline-color: #ffffff;
  }

  /* On a narrow or short screen (a phone, 400 % zoom = 320 x 256 CSS px) the bar
     wraps to two to four lines; kept sticky it would cover half of what is left
     of the viewport on every scroll (WCAG 1.4.10), so there it scrolls away
     with the page. */
  @media (max-width: 760px), (max-height: 480px) {
    [data-topbar] { position: static !important; }
  }

  /* Someone who has asked their system to reduce motion has usually done so
     because movement makes them ill or unable to read. Honour it without
     needing the in-app switch (WCAG 2.3.3). */
  @media (prefers-reduced-motion: reduce) {
    *, *::before, *::after {
      animation-duration: 0.01ms !important;
      animation-iteration-count: 1 !important;
      transition-duration: 0.01ms !important;
      scroll-behavior: auto !important;
    }
  }
  /* ── Phones and small tablets ────────────────────────────────────────────────
     Ported from data-portal's phone layout (its knowledge/reference/mobile-layout.md,
     2026-09-30) on 2026-10-20. Everything is styled inline, and inline styles
     cannot carry a media query: a component opts in with a data-* hook and the
     rules here do the rest, with !important where they override an inline
     style. One breakpoint, 768px. No backticks in this block: globalStyles is
     a template literal.

     These first rules apply at every width. */

  /* Installed standalone with viewport-fit=cover, the page runs under an iPhone
     status bar and notch; the bar pads itself clear of them. Zero elsewhere. */
  [data-topbar] {
    padding-top: env(safe-area-inset-top, 0px);
    padding-left: env(safe-area-inset-left, 0px);
    padding-right: env(safe-area-inset-right, 0px);
  }
  /* The menu button and its panels belong to the phone layout only. */
  [data-nav-toggle] { display: none !important; }
  [data-nav-org] { display: none; }
  /* A word that only the open phone menu shows, beside an icon that is enough
     in the bar (the bell: its aria-label already names it). */
  [data-menu-only] { display: none; }

  /* An identifier with nowhere to break (a sha256, an s3:// path, a file name
     with underscores, an e-mail address): put data-break on the element holding
     it. At every width, since a narrow desktop window overflows too. */
  [data-break] { overflow-wrap: anywhere; min-width: 0; }

  /* TouchReason: why a control is disabled, for a screen with no hover. hover,
     not width: a narrow desktop window still has a mouse. */
  [data-touch-reason] { display: none; }
  @media (hover: none) {
    [data-touch-reason] { display: block; flex-basis: 100%; font-size: 12px; color: #6b7280; line-height: 1.4; }
  }

  /* A file input is as wide as its button plus the name it shows, whatever the
     column. */
  input[type="file"] { max-width: 100%; }

  /* A table whose columns must stay comparable scrolls inside this box (its
     immediate parent) rather than widening the page. The shadows sit over the
     side that has more to scroll to. The alternative, rows read as cards, is
     .responsive-table with data-label on each cell. */
  [data-table-scroll] {
    overflow-x: auto;
    max-width: 100%;
    background:
      linear-gradient(to right, #fff 30%, rgba(255,255,255,0)) left / 32px 100% no-repeat local,
      linear-gradient(to left, #fff 30%, rgba(255,255,255,0)) right / 32px 100% no-repeat local,
      radial-gradient(farthest-side at 0 50%, rgba(0,0,0,0.14), rgba(0,0,0,0)) left / 10px 100% no-repeat scroll,
      radial-gradient(farthest-side at 100% 50%, rgba(0,0,0,0.14), rgba(0,0,0,0)) right / 10px 100% no-repeat scroll;
  }

  @media (max-width: 768px) {
    /* The content column: 24px of padding either side is 13% of a 360px screen.
       Long names (Steiner_wheat_transcriptomics_Res_int_…, a sha256, a URL) break
       anywhere rather than widen the page; overflow-wrap is inherited, and it
       also shrinks the min-content width a flex or grid item is held to. A
       table that scrolls inside its box keeps whole words. */
    [data-content] {
      padding-left: 14px !important;
      padding-right: 14px !important;
      padding-top: 20px !important;
      overflow-wrap: anywhere;
    }
    [data-content] [data-table-scroll] { overflow-wrap: normal; }
    [data-topbar-inner] {
      padding-left: 14px !important;
      padding-right: 14px !important;
      column-gap: 8px !important;
    }

    /* The bar: the logo, the accessibility icon and the menu button on one line;
       the organisation name returns at the head of the open menu. */
    [data-org-name] { display: none !important; }
    [data-a11y-label] { display: none !important; }
    [data-topbar-actions] { display: contents !important; }
    [data-topbar-actions] > button { min-height: 44px !important; min-width: 44px !important; justify-content: center; }
    [data-nav-toggle] { display: inline-flex !important; }

    /* The menu: nav and the right-hand block (the account) as full-width rows
       under the bar, shown only while the button says it is open. Part of the
       bar's flex flow rather than a fixed drawer: nothing to trap focus in, and
       it pushes the page down instead of covering it. */
    [data-nav], [data-nav-right] { display: none !important; }
    [data-nav][data-open="true"], [data-nav-right][data-open="true"] {
      display: flex !important;
      flex-direction: column;
      align-items: stretch;
      flex: 1 1 100%;
      gap: 4px;
      padding: 8px 0;
      border-top: 1px solid rgba(255,255,255,0.3);
      color: #ffffff;
    }
    [data-nav][data-open="true"] { order: 10; }
    [data-nav-right][data-open="true"] { order: 11; }
    [data-nav-org] {
      display: block;
      padding: 0 4px 6px;
      font-size: 12px;
      font-weight: 700;
      letter-spacing: 0.01em;
    }
    /* What an app passes as nav / right is usually a flex row: here it is a column
       of 44px rows, with no separators or caps left over from the bar. */
    [data-nav] > *, [data-nav-right] > * {
      display: flex !important;
      flex-direction: column !important;
      align-items: stretch !important;
      flex-wrap: wrap;
      gap: 4px !important;
      border-left: none !important;
      padding-left: 0 !important;
      margin: 0 !important;
      max-width: none !important;
    }
    [data-nav] > [data-nav-org] { display: block !important; }
    [data-nav] [data-menu-only], [data-nav-right] [data-menu-only] { display: inline; }
    [data-nav] a, [data-nav-right] a, [data-nav-right] button {
      display: flex !important;
      align-items: center;
      min-height: 44px;
      padding: 10px 14px !important;
      border-radius: 8px;
      font-size: 15px !important;
    }
    [data-nav-right] [data-user-label] {
      max-width: none !important;
      white-space: normal !important;
      overflow-wrap: anywhere;
      padding: 4px 14px;
      font-size: 15px !important;
      font-weight: 700;
    }

    /* Tables become cards: each row a block, each cell shows its column name
       from data-label, the header row goes. A cell with no label (row actions)
       shows no name. */
    .responsive-table { width: 100% !important; table-layout: auto !important; }
    .responsive-table thead { display: none; }
    .responsive-table tbody, .responsive-table tr, .responsive-table td,
    .responsive-table tbody th { display: block; width: auto; }
    /* A caption stays the table's name above the cards. */
    .responsive-table caption { display: block; text-align: left; }
    .responsive-table tr {
      border: 1px solid #e5e7eb;
      border-radius: 10px;
      margin-bottom: 10px;
      padding: 4px 2px;
      background: #fff;
    }
    .responsive-table td, .responsive-table td *,
    .responsive-table tbody th, .responsive-table tbody th * {
      white-space: normal !important;
      overflow: visible !important;
      text-overflow: clip !important;
      overflow-wrap: anywhere;
      word-break: break-word;
      max-width: none !important;
    }
    .responsive-table td, .responsive-table tbody th {
      border: none !important;
      padding: 7px 12px !important;
      text-align: left !important;
    }
    .responsive-table td[data-label]::before, .responsive-table tbody th[data-label]::before {
      content: attr(data-label);
      display: block;
      font-size: 10px;
      font-weight: 700;
      text-transform: uppercase;
      letter-spacing: 0.04em;
      color: #6b7280;
      margin-bottom: 2px;
    }
    .responsive-table td:empty { display: none; }

    /* Forms: a fixed pixel width is a fixed pixel width on any screen. Every
       control fills its row and is a 44px target; a row of a control and its
       button (data-field-row) wraps. */
    [data-form-field], [data-form-field] input, [data-form-field] select,
    [data-form-field] textarea {
      width: 100% !important;
      max-width: 100% !important;
    }
    /* Not a file input: it is often hidden behind a button of its own (a 1px
       screen-reader-only box), and 100% of a row would put it past the edge. */
    input:not([type="checkbox"]):not([type="radio"]):not([type="range"]):not([type="file"]), select, textarea {
      width: 100% !important;
      max-width: 100% !important;
      min-height: 44px;
    }
    input[type="file"] { min-height: 44px; }
    [data-field-row] { flex-wrap: wrap !important; }
    [data-field-row] > input { flex: 1 1 200px; }

    /* Targets: 44px without asking, the same rules as data-a11y-targets
       "large". A Toggle grows its button, not its track. */
    button, a, [role="button"], [role="switch"], [role="tab"], summary {
      min-height: 44px !important;
      min-width: 44px !important;
    }
    /* A bare radio or checkbox is 13px; the label wrapping it is the target. */
    label:has(input[type="checkbox"]), label:has(input[type="radio"]) {
      min-height: 44px;
      min-width: 44px;
      display: flex;
      align-items: center !important;
    }
    /* A link that is a control rather than a word in a sentence — the name in a
       card row, a card's title, an entry of a list — is a 44px target too. An
       inline box ignores min-height, so these become inline-flex; a link inside
       a paragraph stays inline (allow-listed by the mobile review). */
    .responsive-table td a, .responsive-table tbody th a,
    h1 a, h2 a, h3 a, h4 a, li > a, [data-tap] {
      display: inline-flex !important;
      align-items: center;
      min-height: 44px;
      max-width: 100%;
    }
    summary { display: flex; align-items: center; }

    /* Two-column grids stack: forms (data-form-grid) and card layouts that are
       not forms (data-col2). min-width 0 lets an item shrink below one long
       name or one table inside it. */
    [data-form-grid], [data-col2] { grid-template-columns: 1fr !important; }
    [data-form-grid] > *, [data-col2] > * { min-width: 0; }

    /* Cards are padded less; nested collapsibles lose their indent (the border
       already shows the nesting) and the subtitle goes under the title. */
    [data-card="default"] {
      padding-top: 16px !important; padding-bottom: 16px !important;
      padding-left: 14px !important; padding-right: 14px !important;
    }
    [data-collapsible-body] { padding-left: 12px !important; }
    [data-collapsible-header] { flex-wrap: wrap; row-gap: 2px !important; }
    [data-collapsible-subtitle] { flex-basis: 100%; padding-left: 22px; }
    [data-content] h1, [data-content] h2, [data-content] h3, [data-content] li,
    [data-content] p, [data-content] dd, [data-content] code,
    [data-collapsible-header] strong, [data-collapsible-header] span:not([aria-hidden]) {
      overflow-wrap: anywhere;
      min-width: 0;
    }

    /* The breadcrumb names the page the title below repeats, so its last item
       is one ellipsised line. */
    [data-breadcrumb] { display: flex; align-items: baseline; min-width: 0; }
    [data-breadcrumb] > * { flex-shrink: 0; }
    [data-breadcrumb] > [data-breadcrumb-current] {
      flex: 0 1 auto;
      min-width: 0;
      overflow: hidden;
      text-overflow: ellipsis;
      white-space: nowrap;
    }

    /* HTML rendered from a document brings its own tables: they scroll inside
       themselves. */
    [data-html] { max-width: 100%; overflow-wrap: anywhere; }
    [data-html] table { display: block; max-width: 100%; overflow-x: auto; }
    [data-html] img, [data-html] pre { max-width: 100%; }
    [data-html] pre { overflow-x: auto; }

    /* Row actions are small on a desktop; here they are 44px targets and the
       row wraps, left-aligned. data-row-actions goes on a wrapper, not on the
       td (a flex cell leaves table layout). */
    .responsive-table td button, .responsive-table tbody th button,
    [data-row-actions] button {
      min-height: 44px !important;
      padding-top: 8px !important;
      padding-bottom: 8px !important;
    }
    [data-row-actions] { display: flex; flex-wrap: wrap !important; gap: 8px; justify-content: flex-start !important; }
    [data-badge] { white-space: normal !important; max-width: 100%; }

    /* Page headers: a title and an action cluster in one row stack, and the
       buttons wrap. A tab strip scrolls on its own if it still cannot fit. */
    [data-page-header] { flex-direction: column !important; align-items: stretch !important; gap: 12px; }
    [data-page-actions] {
      margin-left: 0 !important;
      flex-wrap: wrap !important;
      justify-content: flex-start !important;
    }
    [data-tabstrip] { max-width: 100%; overflow-x: auto; flex-wrap: nowrap !important; }
    [data-tabstrip] button, [data-tabstrip] a { min-height: 44px !important; white-space: nowrap; flex-shrink: 0; }

    /* A log line of "timestamp, then detail" stacks. */
    [data-log-row] { flex-direction: column !important; gap: 2px !important; }

    /* Sheets: full width from the bottom, bounded by 100dvh rather than 90vh so
       the iOS toolbars cannot cover the buttons, clear of the safe areas; the
       actions stacked full width with the primary one nearest the thumb. */
    [data-sheet-overlay] { padding: 0 !important; align-items: flex-end !important; }
    [data-sheet] {
      max-width: 100% !important;
      width: 100% !important;
      max-height: 100vh !important;
      max-height: 100dvh !important;
      border-radius: 16px 16px 0 0 !important;
      border-width: 1px 0 0 !important;
      padding: 20px max(16px, env(safe-area-inset-right, 0px)) calc(20px + env(safe-area-inset-bottom, 0px)) max(16px, env(safe-area-inset-left, 0px)) !important;
    }
    [data-sheet="full"] {
      height: 100vh;
      height: 100dvh;
      border-radius: 0 !important;
      border-width: 0 !important;
      padding-top: calc(16px + env(safe-area-inset-top, 0px)) !important;
    }
    [data-sheet] h2 { overflow-wrap: anywhere; }
    [data-sheet-actions] { flex-direction: column-reverse !important; align-items: stretch !important; }
    [data-sheet-actions] > button { width: 100%; min-height: 44px; justify-content: center; }
  }
`;
